import type { ContentItem } from "@/types/content";

interface InfoPanelProps {
  item: ContentItem;
  isFav: boolean;
  onToggleFav: () => void;
  onShare: () => void;
}

export default function InfoPanel({ item, isFav, onToggleFav, onShare }: InfoPanelProps) {
  const isVideo = item.type === "movie" || item.type === "tv";

  const meta: { label: string; value: string }[] = isVideo
    ? [
        { label: "导演", value: item.director },
        { label: "主演", value: item.cast },
        { label: "类型", value: item.genres.join(" / ") },
        { label: "地区", value: item.region },
        { label: "上映日期", value: item.releaseDate },
      ]
    : [
        { label: "分类", value: item.genres.join(" / ") },
        { label: "来源", value: item.cast },
        { label: "发布时间", value: item.releaseDate },
      ];

  if (isVideo) {
    if (item.duration) meta.push({ label: "时长", value: item.duration });
    if (item.episodes) meta.push({ label: "集数", value: `全 ${item.episodes} 集` });
    if (item.seasons) meta.push({ label: "季数", value: `第 ${item.seasons} 季` });
    if (item.status) meta.push({ label: "更新状态", value: item.status });
    if (typeof item.rating === "number")
      meta.push({ label: "评分", value: item.rating.toFixed(1) });
  }

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onToggleFav}
          className={`px-5 py-2.5 rounded-md text-sm flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap ${
            isFav
              ? "bg-accent-500 text-foreground-950"
              : "bg-primary-500 text-background-50 hover:bg-primary-600"
          }`}
        >
          <i className={isFav ? "ri-bookmark-fill" : "ri-bookmark-line"} />
          {isFav ? "已收藏" : "收藏"}
        </button>
        <button
          type="button"
          onClick={onShare}
          className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm flex items-center gap-2 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-share-forward-line" />
          分享
        </button>
      </div>

      <h2 className="mt-8 font-heading text-2xl text-foreground-950 tracking-wide">内容简介</h2>
      <p className="mt-3 text-sm md:text-base text-foreground-700 leading-8">{item.summary}</p>

      <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 rounded-lg bg-background-100 p-5 md:p-6">
        {meta.map((row) => (
          <div key={row.label} className="flex gap-4 text-sm">
            <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
            <dd className="text-foreground-900">{row.value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-8 space-y-5">
        {item.content.map((paragraph, index) => (
          <p key={index} className="text-sm md:text-base text-foreground-700 leading-8">
            {paragraph}
          </p>
        ))}
      </div>

      {item.tags.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2">
          {item.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}